import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CircularProgress, 
  Grid, 
  TextField, 
  Typography,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import rfidService, { ReaderType } from '../../../../services/rfidService';
import toast from '../../../../utils/toast';
import { parseApiError } from '@/utils/apiFieldErrors';

interface ReaderTypeFormProps {
  isEdit?: boolean;
}

const ReaderTypeForm: React.FC<ReaderTypeFormProps> = ({ isEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formData, setFormData] = useState<Partial<ReaderType>>({
    name: '',
    code: '',
    description: '',
    is_active: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEdit && id) {
      fetchReaderType(id);
    }
  }, [isEdit, id]);

  const fetchReaderType = async (typeId: string) => {
    try {
      setLoading(true);
      const data = await rfidService.getReaderTypeById(typeId);
      setFormData({
        name: data.name,
        code: data.code,
        description: data.description,
        is_active: data.is_active
      });
    } catch (error) {
      console.error('Error fetching reader type:', error);
      toast.error('Failed to fetch reader type details');
      navigate('/rfid/reader-types');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setErrors({});

      if (isEdit && id) {
        await rfidService.updateReaderType(id, formData);
        toast.success('Reader type updated successfully');
      } else {
        await rfidService.createReaderType(formData);
        toast.success('Reader type created successfully');
      }

      navigate('/rfid/reader-types');
    } catch (error: unknown) {
      console.error('Error saving reader type:', error);
      const { fieldErrors, message } = parseApiError(error);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
      } else {
        toast.error(message || `Failed to ${isEdit ? 'update' : 'create'} reader type`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Edit Reader Type' : 'Create Reader Type'}
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  margin="normal"
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  margin="normal"
                  error={!!errors.code}
                  helperText={errors.code || 'Unique code for this reader type'}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  multiline
                  rows={4}
                  error={!!errors.description}
                  helperText={errors.description}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={handleChange}
                      name="is_active"
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate('/rfid/reader-types')}
                    sx={{ mr: 2 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <CircularProgress size={24} />
                    ) : isEdit ? (
                      'Update Reader Type'
                    ) : (
                      'Create Reader Type'
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReaderTypeForm;